import { useState } from 'react';
import {
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Avatar,
  Stack
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import { formatDistanceToNow } from 'date-fns';

/**
 * MessageThread Component
 * Patient-nutritionist messaging view
 */

interface MessageInstance {
  id: string;
  direction: "TO_PATIENT" | "FROM_PATIENT";
  content: string;
  createdAt: string;
  status: "SENT" | "DELIVERED" | "READ";
}

interface MessageThread {
  id: string;
  messages: MessageInstance[];
}

interface MessageThreadProps {
  thread: MessageThread;
  onSend: (content: string) => Promise<void>;
}

export const MessageThreadComponent: React.FC<MessageThreadProps> = ({
  thread,
  onSend
}) => {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!message.trim()) return;

    setSending(true);
    try {
      await onSend(message);
      setMessage("");
    } finally {
      setSending(false);
    }
  };

  return (
    <Paper sx={{ p: 2, height: '70vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
        <Stack spacing={2}>
          {thread.messages.map((msg) => {
            const isFromPatient = msg.direction === "FROM_PATIENT";
            return (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  justifyContent: isFromPatient ? 'flex-end' : 'flex-start'
                }}
              >
                <Box
                  sx={{
                    maxWidth: '70%',
                    display: 'flex',
                    gap: 1,
                    flexDirection: isFromPatient ? 'row-reverse' : 'row'
                  }}
                >
                  <Avatar sx={{ bgcolor: isFromPatient ? 'primary.main' : 'secondary.main' }}>
                    <PersonIcon />
                  </Avatar>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      bgcolor: isFromPatient ? 'primary.light' : 'grey.100'
                    }}
                  >
                    <Typography variant="body2">{msg.content}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={sending}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!message.trim() || sending}
          endIcon={<SendIcon />}
        >
          Send
        </Button>
      </Box>
    </Paper>
  );
};
